/*
 * Konex Drone — Código de descuento del carrito deslizante
 *
 * Cambio principal respecto a la versión anterior:
 * antes se reemplazaba el <cart-drawer> ANTES de comprobar si el cupón era
 * válido. Al reemplazarlo, este mismo elemento quedaba fuera del DOM, así que
 * el mensaje de error se escribía en un nodo ya desconectado y el usuario no
 * lo veía nunca. Ahora el orden es: aplicar -> verificar -> y sólo si el
 * cupón es válido se re-renderiza el carrito.
 */
if (!customElements.get('cart-discount-code')) {
  const MENSAJE_ERROR = 'Código de descuento no válido';
  const MENSAJE_VACIO = 'Introduce un código de descuento';

  class CartDiscountCode extends HTMLElement {
    connectedCallback() {
      this.positionError();
      this.bindEvents();
      this.syncActiveDiscount();
    }

    /*
     * En el tema, el <p> de error va después de la etiqueta del cupón activo.
     * Lo movemos para que quede justo debajo del input, sin tener que editar
     * snippets/cart-drawer.liquid.
     */
    positionError() {
      const field = this.querySelector('.cart-discount__field');
      const error = this.querySelector('.cart-discount__error');
      if (!field || !error) return;
      error.setAttribute('aria-live', 'assertive');
      if (error.previousElementSibling !== field) {
        field.insertAdjacentElement('afterend', error);
      }
    }

    /* ------------------------------------------------------------------ */
    /* Eventos                                                             */
    /* ------------------------------------------------------------------ */

    bindEvents() {
      if (this.dataset.discountEventsBound === 'true') return;
      this.dataset.discountEventsBound = 'true';

      this.addEventListener('click', (event) => {
        const applyButton = event.target.closest('.cart-discount__apply');
        const removeButton = event.target.closest('.remove-discount-btn, .cart-discount__remove');

        if (applyButton) {
          event.preventDefault();
          event.stopPropagation();
          this.applyDiscount();
          return;
        }

        if (removeButton) {
          event.preventDefault();
          event.stopPropagation();
          this.removeDiscount();
        }
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('input[name="discount-code"]')) {
          event.preventDefault();
          this.applyDiscount();
        }
      });

      this.addEventListener('input', (event) => {
        if (event.target.matches('input[name="discount-code"]')) this.clearError();
      });
    }

    /* ------------------------------------------------------------------ */
    /* Estado visual                                                       */
    /* ------------------------------------------------------------------ */

    get input() {
      return this.querySelector('input[name="discount-code"]');
    }

    clearError() {
      const error = this.querySelector('.cart-discount__error');
      if (error) {
        error.textContent = '';
        error.hidden = true;
      }
      this.input?.classList.remove('cart-discount__input--error');
    }

    showError(message) {
      const error = this.querySelector('.cart-discount__error');
      if (error) {
        error.textContent = message;
        error.hidden = false;
        // Reinicia la animación de sacudida aunque el texto sea el mismo
        error.classList.remove('cart-discount__error--shake');
        void error.offsetWidth;
        error.classList.add('cart-discount__error--shake');
      }
      this.input?.classList.add('cart-discount__input--error');
    }

    setLoading(loading) {
      const applyButton = this.querySelector('.cart-discount__apply');
      const removeButton = this.querySelector('.remove-discount-btn, .cart-discount__remove');
      if (applyButton) {
        applyButton.disabled = loading;
        applyButton.classList.toggle('loading', loading);
      }
      if (removeButton) removeButton.disabled = loading;
    }

    renderActiveBadge(title) {
      let badge = this.querySelector('.cart-discount__active');
      if (!badge) {
        badge = document.createElement('div');
        badge.className = 'cart-discount__active';
        const anchor =
          this.querySelector('.cart-discount__error') || this.querySelector('.cart-discount__field');
        anchor?.insertAdjacentElement('afterend', badge);
      }

      if (!title) {
        badge.hidden = true;
        badge.innerHTML = '';
        return;
      }

      badge.hidden = false;
      badge.innerHTML =
        '<span class="cart-discount__active-icon" aria-hidden="true">🏷️</span>' +
        '<span class="cart-discount__active-code"></span>' +
        '<button type="button" class="remove-discount-btn cart-discount__remove" ' +
        'aria-label="Eliminar código de descuento" title="Eliminar código">✕</button>';
      badge.querySelector('.cart-discount__active-code').textContent = title;
    }

    /* ------------------------------------------------------------------ */
    /* Datos                                                               */
    /* ------------------------------------------------------------------ */

    async getCart() {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`No se pudo leer el carrito: ${response.status}`);
      return response.json();
    }

    /* Devuelve el código de cupón realmente activo, o '' si no hay ninguno. */
    activeCode(cart) {
      const entry = (cart?.discount_codes || []).find(
        (item) => item?.code && item?.applicable !== false
      );
      return entry?.code || '';
    }

    /*
     * ¿Se ha aplicado el código que acaba de escribir el usuario?
     * Sólo miramos cart.discount_codes: cart_level_discount_applications
     * también incluye descuentos automáticos, que darían un falso positivo.
     */
    isApplied(cart, code) {
      const objetivo = code.trim().toLowerCase();
      const entry = (cart?.discount_codes || []).find(
        (item) => (item?.code || '').trim().toLowerCase() === objetivo
      );
      if (!entry) return false;
      if (entry.applicable === true) return true;
      if (entry.applicable === false) return false;
      // Si la tienda no devuelve "applicable", deducimos por el importe.
      return Number(entry.amount || 0) > 0 || Number(cart?.total_discount || 0) > 0;
    }

    async updateDiscount(discount) {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          discount,
          sections: 'cart-drawer',
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) return null; // Shopify rechazó el cupón: no es un fallo fatal
      return response.json();
    }

    replaceCartDrawer(data, keepOpen = true) {
      const sectionHtml = data?.sections?.['cart-drawer'];
      if (!sectionHtml) throw new Error('Shopify no devolvió la sección cart-drawer');

      const currentDrawer = document.querySelector('cart-drawer');
      if (!currentDrawer) throw new Error('No se encontró el carrito deslizante');

      const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const newDrawer = parsed.querySelector('cart-drawer');
      if (!newDrawer) throw new Error('El carrito actualizado no es válido');

      const wasOpen =
        keepOpen || currentDrawer.classList.contains('active') || currentDrawer.hasAttribute('open');

      if (wasOpen) {
        newDrawer.classList.add('active', 'animate');
        newDrawer.setAttribute('open', '');
      }

      currentDrawer.replaceWith(newDrawer);
      if (wasOpen && typeof newDrawer.open === 'function') {
        requestAnimationFrame(() => newDrawer.open());
      }
      return newDrawer;
    }

    async syncActiveDiscount() {
      try {
        const cart = await this.getCart();
        this.renderActiveBadge(
          this.activeCode(cart) || cart?.cart_level_discount_applications?.[0]?.title || ''
        );
      } catch (error) {
        console.error('No se pudo sincronizar el descuento:', error);
      }
    }

    /* ------------------------------------------------------------------ */
    /* Acciones                                                            */
    /* ------------------------------------------------------------------ */

    async applyDiscount() {
      const code = this.input?.value.trim() || '';
      this.clearError();

      if (!code) {
        this.showError(MENSAJE_VACIO);
        this.input?.focus();
        return;
      }

      this.setLoading(true);

      let codigoPrevio = '';
      try {
        codigoPrevio = this.activeCode(await this.getCart());
      } catch (error) {
        console.error(error);
      }

      try {
        const data = await this.updateDiscount(code);
        const cart = await this.getCart();

        if (data && this.isApplied(cart, code)) {
          // Válido: ahora sí re-renderizamos el carrito con totales y descuento
          this.replaceCartDrawer(data, true);
          return;
        }

        // No válido: dejamos el carrito intacto y restauramos el cupón anterior
        await this.updateDiscount(codigoPrevio);
        this.renderActiveBadge(codigoPrevio);
        this.showError(MENSAJE_ERROR);
        this.input?.focus();
        this.input?.select();
      } catch (error) {
        console.error(error);
        this.showError(MENSAJE_ERROR);
      } finally {
        this.setLoading(false);
      }
    }

    async removeDiscount() {
      this.clearError();
      this.setLoading(true);
      try {
        const data = await this.updateDiscount('');
        if (data) {
          this.replaceCartDrawer(data, true);
          return;
        }
        this.renderActiveBadge('');
      } catch (error) {
        console.error(error);
        this.showError('No se pudo eliminar el código de descuento');
      } finally {
        this.setLoading(false);
      }
    }
  }

  customElements.define('cart-discount-code', CartDiscountCode);
}
